import { AuthBrandTagline } from '../../components/AuthBrandTagline';
import { AuthLanguagePicker } from '../../components/AuthLanguagePicker';
import './auth-polish.css';
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import { useLocale } from '../../hooks/useLocale';
import { NmCard } from '../../components/NmCard';
import { LoginPage } from '../LoginPage';
import { RegisterPage } from '../RegisterPage';
import logoUrl from '@assets/logo.png';
import {
  clearPendingRegistrationConnections,
  hasPendingRegistrationConnections,
  markPendingRegistrationConnections,
} from '../../utils/pendingRegistrationConnections';

type AuthTab = 'signin' | 'signup';


interface PendingRegistration {
  mobile: string;
  token: string;
}

/** Auth shell with Sign In / Create Account tabs — mirrors vo2-bridge AuthPage. */
export function AuthPage({ mode }: { mode: 'login' | 'register' }) {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const auth = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState<AuthTab>(mode === 'register' ? 'signup' : 'signin');
  const [prefillMobile, setPrefillMobile] = useState('');
  const [pendingRegistration, setPendingRegistration] = useState<PendingRegistration | null>(null);
  const [completingRegistration, setCompletingRegistration] = useState(
    () => hasPendingRegistrationConnections(),
  );

  useEffect(() => {
    setTab(mode === 'register' ? 'signup' : 'signin');
  }, [mode]);

  const switchToSignUp = useCallback(
    (mobile?: string) => {
      if (mobile) setPrefillMobile(mobile);
      setTab('signup');
      navigate('/register', { replace: true });
    },
    [navigate],
  );

  const switchToSignIn = useCallback(
    (mobile?: string) => {
      if (mobile) setPrefillMobile(mobile);
      setPendingRegistration(null);
      setTab('signin');
      navigate('/login', { replace: true });
    },
    [navigate],
  );

  const handleNeedsRegistration = useCallback((mobile: string, token: string) => {
    setPendingRegistration({ mobile, token });
    setPrefillMobile(mobile);
    setTab('signup');
    navigate('/register', { replace: true });
  }, [navigate]);

  if (auth.isLoading) return null;
  const pendingConnections = completingRegistration || hasPendingRegistrationConnections();
  if (auth.isAuthenticated && !pendingConnections) return <Navigate to="/calendar" replace />;

  return (
    <div style={styles.root} className="auth-page" dir={isRtl ? 'rtl' : 'ltr'}>
      <div style={styles.blobTop} />
      <div style={styles.blobBottom} />

      <div className="auth-center-wrap" style={styles.centerWrap}>
        <NmCard variant="raised-lg" className="auth-surface" style={styles.card}>
          <AuthLanguagePicker />
          <div className="auth-brand-row" style={styles.logoRow}>
            <img src={logoUrl} alt={t('brand.name')} style={styles.brandLogo} className="auth-brand-logo" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <AuthBrandTagline style={styles.brandTagline} />
            </div>
          </div>

          <div className="nm-auth-tabs" data-active={tab} role="tablist">
            <span className="nm-auth-tabs__thumb" aria-hidden />
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'signin'}
              className={`nm-auth-tab ${tab === 'signin' ? 'nm-auth-tab--active' : ''}`}
              onClick={() => switchToSignIn()}
            >
              {t('auth.tabLogin')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'signup'}
              className={`nm-auth-tab ${tab === 'signup' ? 'nm-auth-tab--active' : ''}`}
              onClick={() => switchToSignUp()}
            >
              {t('auth.tabRegister')}
            </button>
          </div>

          <div className="nm-divider" />

          {tab === 'signin' ? (
            <LoginPage
              key={`signin-${prefillMobile}`}
              initialMobile={prefillMobile}
              embedded
              onLogin={auth.login}
              onLoginByOtp={auth.loginByOtp}
              onSendOtp={auth.sendOtp}
              onCheckMobile={auth.checkMobile}
              onSwitchToRegister={switchToSignUp}
              onNeedsRegistration={handleNeedsRegistration}
            />
          ) : (
            <RegisterPage
              key={pendingRegistration?.token ?? `signup-${prefillMobile}`}
              initialMobile={pendingRegistration?.mobile ?? prefillMobile}
              initialToken={pendingRegistration?.token ?? ''}
              userId={auth.user?.id}
              onRegister={auth.register}
              onCompleteRegister={auth.completeRegister}
              onCheckMobile={auth.checkMobile}
              onSwitchToSignIn={switchToSignIn}
              onAccountCreated={() => {
                markPendingRegistrationConnections();
                setCompletingRegistration(true);
              }}
              onFinish={() => {
                clearPendingRegistrationConnections();
                setCompletingRegistration(false);
                navigate('/calendar', { replace: true });
              }}
              onAccountCreationFailed={() => {
                clearPendingRegistrationConnections();
                setCompletingRegistration(false);
              }}
            />
          )}
        </NmCard>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100dvh',
    background: 'var(--surface-0)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: 'var(--sp-4)',
    paddingTop: 'calc(var(--sp-4) + env(safe-area-inset-top))',
    paddingBottom: 'calc(var(--sp-4) + env(safe-area-inset-bottom))',
  },
  blobTop: {
    position: 'absolute',
    top: -120,
    right: -120,
    width: 360,
    height: 360,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(223,32,99,0.08) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  blobBottom: {
    position: 'absolute',
    bottom: -100,
    left: -100,
    width: 280,
    height: 280,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(31,174,99,0.07) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  centerWrap: {
    width: '100%',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--sp-5)',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--sp-4)',
  },
  brandLogo: {
    width: 96,
    height: 32,
    padding: 6,
    borderRadius: 6,
    background: '#e8ecf2',
    objectFit: 'contain' as const,
    flexShrink: 0,
  },
  brandTagline: {
    fontSize: 'var(--fs-body)',
    fontFamily: 'var(--font-display)',
    fontWeight: 700,
    color: 'var(--fg-2)',
    margin: 0,
    lineHeight: 1.35,
  },
};
